import { memo } from 'react';


type SafeRecord<K extends string | number | symbol, T> = T extends never ? never : Record<K, T>
export type Nullish<T, U = {[K in keyof T]: SafeRecord<K, Nullish<T[K]>>}> = T & (null | (T extends object ? U[keyof U] : never))
export type NonNullish<T> = T extends object ? (T & { [K in keyof T]: NonNullish<T[K]>}) : NonNullable<T>
export type Narrowable<T> = NonNullish<T> | Nullish<T>

export function isNonNullish<T>(thing: T): thing is NonNullish<T> {
    return thing !== undefined && thing !== null && Object.values(thing).every(isNonNullish)
}

export const genericMemo: <T>(component: T) => T = memo

export type Responsible = 'no_idea' | 'someone_else' | 'myself' | number

export type Attachment = {
    ContentType: string
    TemporaryUrl: string
    Comment: string
    CurrencyCode: string
    Type: number
    AttachmentStatus: number
    UploadedBy: string
    ImageDate: string
    TransactionDate: string | null
    // 0 when Visma has no scanned amount (most attachments) — it omits the field and the frame
    // fills a zero, so treat falsy, not null, as "unknown".
    TotalAmount: number | null
    // Fields Visma's scan extracted onto the attachment (partial, per document type). Used as
    // editor defaults for a fresh draft; the vendor identity is the payee only for supplier
    // invoices (Type 0) — on a reimbursement receipt (Type 1) it's just the shop, not who's paid.
    SupplierName: string | null
    SupplierCorporateIdentityNumber: string | null
    InvoiceNumber: string | null
    Ocr: string | null
    DueDate: string | null
    DefaultReimburseeId: string | null
    Url: string
}

export type DraftRow = {
    cost_center_item_id: string | null
    project_id: string | null
    expense_account: number | null
    amount: number | null
}

export type ValidDraftRow = {
    cost_center_item_id: string
    project_id: string
    expense_account: number
    amount: number
}

export type DraftStatus = "uninitialized" | "initialized" | "finalized"

export type Draft = {
    draft_id: string
    status: DraftStatus
    type: number | null
    invoice_date: string | null
    message: string | null
    ocr_enabled: boolean
    ocr_number: string | null
    reimbursee_id: string | null
    supplier_id: string | null
    invoice_number: string | null
    due_date: string | null
    // ISO 4217 code the row amounts are expressed in ('SEK' unless the invoice is foreign).
    currency_code: string
    version: number
    probably_payed_by_us: boolean | null
    attachment: Attachment
    role_ids_attested: number[]
    rows: DraftRow[]
}

export type DraftToSave = {
    draft_id: string
    status: DraftStatus
    type: number | null
    invoice_date: string | null
    message: string | null
    ocr_enabled: boolean
    ocr_number: string | null
    reimbursee_id: string | null
    supplier_id: string | null
    invoice_number: string | null
    due_date: string | null
    currency_code: string
    rows: DraftRow[]
}

export type Supplier = {
    Id: string
    Name: string
//    IsActive: boolean
    CorporateIdentityNumber: string | null
    // The supplier's own currency (null/empty means SEK). An invoice may only be booked on a
    // supplier whose currency matches it — Visma rejects a mismatch.
    CurrencyCode: string | null
    // Where the money goes: a giro for a domestic supplier, an IBAN for a foreign one. Shown in
    // the picker so it can be checked before attesting, not discovered afterwards.
    GiroNumber: string
    BankIban: string | null
    BankBic: string | null
    // A domestic supplier paid to an ordinary bank account instead of a giro, as an utlägg is.
    BankAccountNumber: string | null
    Clearing: string | null
}

// Someone a kvitto på utlägg is paid to: a private person by bank account, or else a company or
// association by its one giro. The bank account, when set, is the route.
export type Reimbursee = {
    Id: string
    BankAccountNumber: string | null
    BankgiroNumber: string | null
    Name: string
    PlusgiroNumber: string | null
//    IsActive: boolean
}

export type Role = {
    id: number
    name: string
}

export type Project = {
    project_id: string
    ProjectNumber: string
    ProjectName: string
    attestant_role_ids: number[]
}

export type Article = {
    article_id: string,
    IsActive: boolean,
    Number: string,
    Name: string,
    NetPrice: number,
    CodingId: string
}

export type ProjectWithIncomeAccounts = Project & {income_account_numbers: number[]}

export type ProjectWithArticles = ProjectWithIncomeAccounts & {articles: Article[]}

export type ProjectWithExpenseAccounts = Project & {expense_account_numbers: number[]}

export type CostCenterItem<ProjectType = Project> = {
    cost_center_item_id: string
    projects: ProjectType[]
    CostCenterItemName: string
    CostCenterItemShortName: string
}

export type ExpensesData = {
    drafts: Draft[]
    draft_responsibles: Responsible[]
    start_index: number
    suppliers: Supplier[]
    reimbursees: Reimbursee[]
    other_roles: Role[]
    // Whether underlag that nobody holds yet belong in this role's own list (Inställningar →
    // Företag, "Nya underlag"). The "visa även" checkbox shows them regardless.
    receives_new_underlag: boolean
    // The company's fiscal years in Visma (YYYY-MM-DD, inclusive). Visma books into none other.
    fiscal_years: { start: string, end: string }[]
    cost_center_items: CostCenterItem<ProjectWithExpenseAccounts>[]
    // number -> name for every expense account any project references; sent once
    // per payload, from the same DB read as the per-project numbers (so consistent).
    accounts: Record<number, string>
}

export type Account = {
    account_number: number
    account_name: string
}

export type Customer = {
    Id: string,
    Name: string,
    EmailAddress: string,
    CustomerNumber: string
}

export type IncomesData = {
    customers: Customer[],
    cost_center_items: CostCenterItem<ProjectWithArticles>[],
    official_income_accounts: Account[]
}

export type CustomerInvoiceRow = {
    costCenterItemId: string | null,
    projectId: string |null,
    articleId: string | null,
    text: string,
    quantity: number | null,
    unitPrice: number | null
}

export type CustomerPaymentInfo = {
    IsCreditInvoice: boolean,
    RemainingAmount: number,
    DueDate: string,
    PaymentReminderIssued: boolean,
    PaymentDate: string,
    PaymentStatus: number,
    CustomerInvoiceId: string
}

export type Income = {
    VoucherDate: string,
    AccountDescription: string[],
    IncomeAmount: number[],
    Description: string[] | string,
    CustomerName: string | null,
    CreditedBy: {CreditInvoiceId: string}[] | null,
    PaymentInfo: CustomerPaymentInfo | null
}

export type SupplierPaymentInfo = {
    RemainingAmount: number,
    DueDate: string,
    IsOverdue: boolean,
    PaymentDate: string,
    PaymentStatus: number,
}

export type Expense = {
    AccountDescription: string[],
    ExpenseAmount: number[],
    VoucherDate: string,
    VoucherText: string | null,
    NumberAndNumberSeries: string,
    Attachments: {AttachmentIds: string[]} | null,
    SupplierName: string | null,
    Message: string | null,
    InvoiceNumber: string | null,
    OutlayNumber: string | null,
    Description: number,
    PaymentInfo: SupplierPaymentInfo | null
}

// How the whole kostnadsställe is doing against its budget this verksamhetsår — every project under
// it, this year only, because that is what a budget is set per. Fetched on its own
// (/overview/cost-center-status) rather than with the rows, so it appears as soon as a
// kostnadsställe is chosen. null when Visma has no single fiscal year covering today; `budget` null
// means none was entered, which is shown as such rather than as zero.
export type BudgetStatus = {
    fiscal_year_start: string,
    fiscal_year_label: string,
    budget: number | null,
    result: number,
}

export type OverviewData = {
    incomes: Income[],
    expenses: Expense[],
}

export type EventInfo = {
    event_id: number,
    name: string,
    date: string,
    type: string
}

export type EventorInvoiceImportData = {
    events: EventInfo[],
    cost_center_items: CostCenterItem<ProjectWithIncomeAccounts>[],
    official_income_accounts: Account[]
}

export type InvoiceInfo = {
    url: string,
    org_name: string,
    status: string,
    total_amount: number,
    invoice_number: number,
    already_imported: boolean,
    visma_paid: boolean,
    import_status?: string
}

export type ListEventData = { invoices: InvoiceInfo[] }

// An ordinary event's invoice taken apart (invoice_split.py): its amount by part, and the lines no
// part could be found for.
export type InvoiceSplitPart = { key: string, label: string, amount: number, lines: number }
export type InvoiceSplit = {
    invoice_number: number,
    parts: InvoiceSplitPart[],
    unsplit_lines: { text: string, amount: number }[],
    lines: number,
    // Every line read, kept so the invoice can be taken apart again (with a stated late surcharge)
    // without reading it from Eventor again.
    read_lines: { text: string, amount: number }[],
    // How the entry lines were taken apart, counted per amount and split.
    entry_amounts: EntryAmount[],
}

export type EntryAmount = { amount: number, ordinary: number, late: number, unsplit: boolean, lines: number }

export type UpdateEventData = { invoice_number: number, import_status: string }

export type MemberId = {
    family_name: string
    given_names: string[]
    year_of_birth: number | null
}

export type EventorMemberInfo = {
    id: MemberId,
    person_id: string,
    last_modified: string,
    mobile_phone_number: string | null
    email_address: string | null
}

export type BasicMemberInfo = {
    eventor: EventorMemberInfo
    visma_customer_id: string | null
    eventor_updated_later_than_visma: boolean
}

// PrepaidCompetition reconciliation (Eventor orders vs Visma invoices); mirrors sync_prepaid_competition.py.
export type SyncStatus =
    'in_sync' | 'eventor_only' | 'visma_only' | 'amount_drift'
    | 'payment_due' | 'partial_payment_due' | 'overpaid_in_eventor'

export type SyncRow = {
    reference_number: number,
    org_name: string | null,
    eventor_amount: number | null,
    visma_amount: number | null,
    eventor_paid_amount: number | null,
    visma_paid_amount: number | null,
    sync_status: SyncStatus,
}

export type PrepaidCompetitionSyncData = {
    rows: SyncRow[],
    // This person's stored "Dölj rader i synk" choice, for this company.
    hide_in_sync: boolean,
}

// Swish/Zettle bookkeeping config maps (see docs/zettle-swish.md §5). A "location" is where
// income is booked: an account plus its cost-center item and project.
export type SwishLocation = {
    income_account: number,
    cost_center_item_id: string,
    project_id: string,
}

export type SwishCategoryMapping = SwishLocation & {
    category_uuid: string,
    category_name: string,
}

// Poster QR amount: payer enters it (open), fixed & locked, or prefilled but editable.
export type SwishAmountMode = 'open' | 'fixed' | 'prefilled'

export type SwishMessageMapping = SwishLocation & {
    message: string,
    // This poster stands in for the POS when it is down, so a Zettle sale keyed in afterwards is
    // not money that went missing — it was already paid, and booked, through the poster.
    is_zettle_backup: boolean,
    amount_mode: SwishAmountMode,
    amount: number | null,   // whole kronor for fixed/prefilled; null for open
}

export type ZettleCategory = { uuid: string, name: string }

// Where a redeemed presentkort's cost is booked — an expense account, not an income one.
export type SwishCostLocation = {
    expense_account: number,
    cost_center_item_id: string,
    project_id: string,
}

export type SwishSettingsData = {
    category_mappings: SwishCategoryMapping[],
    message_mappings: SwishMessageMapping[],
    default_location: SwishLocation | null,
    giftcard_cost_location: SwishCostLocation | null,
    // Expense-location picker source for the presentkort cost, and its account names.
    expense_cost_center_items: CostCenterItem<ProjectWithExpenseAccounts>[],
    expense_accounts: Record<number, string>,
    zettle_categories: ZettleCategory[],
    swish_number_configured: boolean,   // is swish_number set? (in swish_config; gates poster generation)
    // The Swish message the POS terminals' QR writes — what identifies a till payment. null = unset,
    // which means every payment without a poster code counts as one.
    pos_message: string | null,
    // Income-location picker source (account-based, not article-based — Swish income isn't articles).
    cost_center_items: CostCenterItem<ProjectWithIncomeAccounts>[],
    official_income_accounts: Account[],
    // Complete GUID -> name maps (all cost-centres/projects), so a mapping whose pair is outside
    // every role's scope — and thus absent from the tree — still resolves to a name, not a GUID.
    cost_center_names: Record<string, string>,
    project_names: Record<string, string>,
}

export type SwishDecision = 'nothing' | 'book' | 'skip' | 'flag_excess' | 'flag_changed'

export type SwishSplitRow = SwishLocation & { amount: number, in_scope: boolean }

// One transaction row in a day's expandable detail. Deposit statuses: booked_message | booked |
// unmatched | ambiguous | blocked | externally_booked. Purchase statuses: booked |
// registered_not_paid | covered_by_backup | refund | unmapped | blocked | zero_value.
export type SwishDepositItem = { amount: number, reference: string | null, status: string }
export type SwishPurchaseItem = { amount: number, label: string, status: string,
                                  // The Swedish date the sale was rung up on; a batch spans several.
                                  sales_day: string | null }

export type SwishDayPreview = {
    date: string,
    decision: SwishDecision,
    total: number,
    split: SwishSplitRow[],
    clearing_shortfall: Record<string, number>,
    // The bank's Swish fee on the batch's payments (null when the club does not book it).
    fee: number | null,
    flags: { kind: string, detail: string }[],
    deposits: SwishDepositItem[],
    purchases: SwishPurchaseItem[],
    // The days that actually sold into this batch — the bank date alone is not what it covers.
    sales_days: string[],
}

// The card and kassa rails (docs/zettle-swish.md §20-§21). book: will post · skip: Eko already
// booked it · external: booked by hand outside Eko · waiting: the payout has not reached the bank ·
// outside: it reached the bank after t.o.m. · blocked: something must be fixed first (see flags) ·
// withheld: reaches outside the role's scope · nothing: nothing to book.
export type ZettleRailDecision = 'nothing' | 'book' | 'skip' | 'external' | 'waiting' | 'outside' | 'blocked' | 'withheld'

// One Zettle payout: the card sales since the payout before, booked on the day it reached the bank.
export type ZettlePayoutPreview = {
    sent: string,                // when Zettle sent it
    bank_date: string | null,    // when the bank booked it; null until it arrives
    decision: ZettleRailDecision,
    gross: number,
    fee: number,
    payout: number,
    payments: number,
    sales_days: string[],
    split: SwishSplitRow[],
    flags: string[],
}

// One sales day's kassa: cash, Swish refunds paid from the box, redeemed presentkort.
export type ZettleKassaPreview = {
    date: string,
    decision: ZettleRailDecision,
    cash: number,
    giftcards: number,
    cash_sales: number,
    cash_refunds: number,
    swish_refunds: number,
    giftcard_count: number,
    split: SwishSplitRow[],
    flags: string[],
}

export type SwishSyncPreview = {
    configured: boolean,
    from_date?: string,
    to_date?: string,
    days?: SwishDayPreview[],
    payouts?: ZettlePayoutPreview[],
    kassa_days?: ZettleKassaPreview[],
    // Card sales Zettle holds but has not paid out yet — waiting, not missing.
    card_waiting?: { count: number, amount: number },
    // The payout ledger could not be read (e.g. a key without finance access); the card rail is off.
    ledger_error?: string | null,
}

export type SyncRail = 'swish' | 'kort' | 'kassa'


// --- Registrations against invoices (read-only diagnostic) ---

// One row of the report's summary: how much of a product each side counts. The product's identity
// is its unit price, the same identity Eko books by.
export type PriceSummary = { unit_price_ore: number, registered: number, invoiced: number }

// Certain: even counting every team that merely mentions this club as its own, it has paid for more
// than exist. `listed` is that count, so the reader can check the claim.
export type OverInvoiced = {
    club: string, unit_price_ore: number, count: number,
    invoiced: number, listed: number, references: number[],
}

// Certain the other way: teams only this club can have paid for, more of them than it was billed.
export type UnderInvoiced = {
    club: string, unit_price_ore: number, count: number, invoiced: number, solo: number,
}

// Real but unattributable: clubs tied together by teams they share. Naming one of them would credit
// the wrong member club, so the group is named instead.
export type GroupDiscrepancy = {
    clubs: string[], unit_price_ore: number, net: number,
    invoiced: number, registered: number, references: number[],
}

export type RegistrationLine = {
    description: string, unit_price_ore: number, count: number, shared_with: string[],
}
export type InvoiceLine = { reference: number, unit_price_ore: number, quantity: number }
export type ClubEvidence = {
    club: string, registrations: RegistrationLine[], invoice_rows: InvoiceLine[],
    // Which club page a removal would open, and at which prices this club holds services at all.
    // Enough to decide whether to *offer* one; the rows themselves are read fresh when it is used.
    organisation_id: number | null, service_prices_ore: number[],
}

// One ordered-service row on a club's entry page. `status` is Eventor's own word — 'Ej beställt',
// 'Beställd', 'Betald' — and the only visible trace of which order the row belongs to.
export type ServiceRow = {
    request_id: number, name: string, quantity: number,
    unit_price_ore: number, ordered_at: string, status: string,
}

// A club holding services nobody ordered. Reported apart from the invoicing findings because it
// wants the opposite action: such a row is invisible in Anmälningsöversikt and will be billed the
// next time anyone orders for that club, so it is ordered or removed, not credited.
export type UnorderedServices = {
    club: string, organisation_id: number, services: string[],
}

export type ReconciliationReport = {
    event_id: number,
    // How many team fees were checked against the team's own page rather than taken from the club
    // page, which is cheap to read and can be stale.
    verified: number,
    reconciliation: {
        prices: PriceSummary[],
        over_invoiced: OverInvoiced[],
        under_invoiced: UnderInvoiced[],
        groups: GroupDiscrepancy[],
    },
    evidence: ClubEvidence[],
    unordered_services: UnorderedServices[],
}

// How a run is going, or how it went. The report takes minutes, so it is started and then asked
// after rather than waited for — which is what lets it survive a reload, a tab switch, or anything
// in the network deciding a long connection has gone on long enough.
//
// `candidates` is non-empty only when the payment event did not resolve to exactly one competition.
// `accounts` is keyed by unit price in öre and says what that price would book as — configuration,
// not history: it says nothing about whether the invoice has reached Visma.
export type ReconciliationStatus = {
    run_id: string,
    state: 'running' | 'done' | 'failed' | 'unknown',
    phase: string,
    done: number,
    total: number,
    message: string | null,
    report: ReconciliationReport | null,
    candidates: number[],
    // Its parts, because one price may book on several locations — a late entry is an entry fee
    // plus a late charge. One part is the ordinary case and reads as it always did.
    accounts: Record<string, { parts: { amount_ore: number, account_number: number,
                                        account_name: string }[] }>,
    // When the run was started and when it ended, in the club's timezone with its offset. A report
    // outlives the page that asked for it — a reload, a re-login or a change of role finds it
    // again — so how old it is has to be on it.
    started_at: string | null,
    finished_at: string | null,
}
